import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, ExternalLink, ImagePlus, Loader2, MapPin, Save, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { StyleChips } from '@/components/marketplace/DirectoryFilters';
import { VerifiedBadge } from '@/components/marketplace/VerifiedBadge';
import { AdminAvailabilityCard } from '@/components/admin/AdminAvailabilityCard';
import { AdminReviewsCard } from '@/components/admin/AdminReviewsCard';
import { studioGetProfile, studioUpdateProfile, studioUploadAsset, studioPortfolioCandidates, apiErrorMessage } from '@/lib/api';
import { clampedAccentHsl, LXRA_GOLD_HEX } from '@/lib/studioAccent';
import { ADMIN_MY_PROFILE as T } from '@/constants/testIds';

const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';
const Card = ({ title, hint, children }) => (
  <section className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6 space-y-5">
    <div>
      <h3 className="font-display text-xl tracking-[0.06em] text-[hsl(var(--lxra-fg))]">{title}</h3>
      {hint && <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">{hint}</p>}
    </div>
    {children}
  </section>
);
const statusTone = { approved: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-200', pending: 'border-[#C9A45C]/25 bg-[#C9A45C]/10 text-[#D7B46A]', suspended: 'border-red-500/25 bg-red-500/10 text-red-200' };
const MAX_PORTFOLIO = 24;

const toForm = (p) => ({
  handle: p.handle || '', name: p.name || '', tagline: p.tagline || '', bio: p.bio || '', styles: p.styles || [],
  service_area: p.service_area || '', home_base_label: p.home_base?.label || '', travel_radius_miles: p.travel_radius_miles ?? 50,
  accent_color: p.accent_color || LXRA_GOLD_HEX, instagram: p.instagram || '', website: p.website || '', portfolio: p.portfolio || [],
});

/** Photographer's own Marketplace profile (LXRA owner edits whichever studio they are viewing as). */
export const AdminMyProfileTab = () => {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // 'logo' | 'cover'
  const logoRef = useRef(null);
  const coverRef = useRef(null);

  useEffect(() => {
    studioGetProfile().then((p) => { setProfile(p); setForm(toForm(p)); }).catch(() => toast.error('Could not load your profile.'));
    studioPortfolioCandidates().then(setCandidates).catch(() => setCandidates([]));
  }, []);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const accentHsl = useMemo(() => clampedAccentHsl(form?.accent_color), [form?.accent_color]);
  const portfolioIds = useMemo(() => new Set((form?.portfolio || []).map((p) => p.image_id || p.image_url)), [form?.portfolio]);

  const togglePortfolio = (img) => {
    setForm((f) => {
      const key = img.id;
      const exists = f.portfolio.some((p) => (p.image_id || p.image_url) === key);
      if (exists) return { ...f, portfolio: f.portfolio.filter((p) => (p.image_id || p.image_url) !== key) };
      if (f.portfolio.length >= MAX_PORTFOLIO) { toast.error(`Portfolio holds up to ${MAX_PORTFOLIO} photos.`); return f; }
      return { ...f, portfolio: [...f.portfolio, { image_id: img.id, image_url: img.variants?.web || img.image_url, caption: img.caption || '' }] };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, handle: form.handle.trim().toLowerCase(), travel_radius_miles: Number(form.travel_radius_miles) };
      const updated = await studioUpdateProfile(payload);
      setProfile(updated);
      setForm(toForm(updated));
      toast.success('Profile saved.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save your profile.'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const res = await studioUploadAsset(kind, file);
      setProfile((p) => ({ ...p, [`${kind}_url`]: res.url }));
      toast.success(kind === 'logo' ? 'Logo updated.' : 'Cover updated.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Upload failed.'));
    } finally {
      setUploading(null);
      if (kind === 'logo' && logoRef.current) logoRef.current.value = '';
      if (kind === 'cover' && coverRef.current) coverRef.current.value = '';
    }
  };

  if (!profile || !form) return <Skeleton className="h-96 rounded-2xl bg-[#141416]" />;

  const isLive = profile.status === 'approved';
  return (
    <form onSubmit={handleSave} data-testid={T.root} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-[hsl(var(--lxra-muted))]">Your public Marketplace profile.</p>
          <Badge data-testid={T.statusBadge} variant="outline" className={`uppercase text-[10px] tracking-[0.1em] ${statusTone[profile.status] || ''}`}>{profile.status}</Badge>
          {profile.verified && <VerifiedBadge />}
        </div>
        <div className="flex items-center gap-2">
          {isLive ? (
            <Button asChild variant="outline" data-testid={T.viewPublicLink} className="h-10 rounded-lg border-[hsl(var(--lxra-border))] bg-transparent text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] text-xs uppercase tracking-[0.1em]">
              <Link to={`/p/${profile.handle}`} target="_blank"><ExternalLink className="h-4 w-4 mr-1.5" /> View public page</Link>
            </Button>
          ) : (
            <span className="text-xs text-[hsl(var(--lxra-muted))]">Public page unlocks after approval</span>
          )}
          <Button type="submit" disabled={saving} data-testid={T.saveButton} className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save profile</>}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="IDENTITY" hint="How you appear in the directory and at /p/<handle>.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className={labelClass}>Studio name</Label>
              <Input required value={form.name} onChange={(e) => set('name')(e.target.value)} data-testid={T.nameInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Handle</Label>
              <div className="flex items-center">
                <span className="h-10 inline-flex items-center px-2.5 rounded-l-lg border border-r-0 border-[hsl(var(--lxra-border))] bg-[#0A0A0A] text-xs text-[hsl(var(--lxra-muted))]">/p/</span>
                <Input required value={form.handle} onChange={(e) => set('handle')(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} disabled={profile.kind === 'lxra'} data-testid={T.handleInput} className={`${fieldClass} rounded-l-none lxra-mono`} />
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className={labelClass}>Tagline</Label>
            <Input value={form.tagline} onChange={(e) => set('tagline')(e.target.value)} maxLength={160} placeholder="One line that says what you shoot and how it feels" data-testid={T.taglineInput} className={fieldClass} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelClass}>Bio</Label>
            <Textarea value={form.bio} onChange={(e) => set('bio')(e.target.value)} maxLength={3000} rows={6} placeholder="Your story, your approach, who you love working with." data-testid={T.bioInput} className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] focus-visible:ring-[hsl(var(--lxra-gold))]" />
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Styles (up to 10)</Label>
            <StyleChips styles={profile.styles_available || []} selected={form.styles} onToggle={(s) => set('styles')(form.styles.includes(s) ? form.styles.filter((x) => x !== s) : [...form.styles, s])} testId={T.styleChips} chipTestId={T.styleChip} size="sm" />
          </div>
        </Card>

        <div className="space-y-6">
          <Card title="WHERE YOU SHOOT" hint="Home base is geocoded so clients can search by distance.">
            <div className="space-y-1.5">
              <Label className={labelClass}>Home base</Label>
              <Input value={form.home_base_label} onChange={(e) => set('home_base_label')(e.target.value)} placeholder="e.g. Bethesda, MD" data-testid={T.homeBaseInput} className={fieldClass} />
              {profile.home_base?.lat != null && (
                <p data-testid={T.geocodedLabel} className="text-[11px] text-[hsl(var(--lxra-muted))] inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-[hsl(var(--lxra-gold))]" /> Pinned to {profile.home_base.label}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Service area (shown on cards)</Label>
              <Input value={form.service_area} onChange={(e) => set('service_area')(e.target.value)} placeholder="e.g. Montgomery County & DC" data-testid={T.serviceAreaInput} className={fieldClass} />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className={labelClass}>Travel radius</Label>
                <span data-testid={T.radiusReadout} className="text-xs lxra-mono text-[hsl(var(--lxra-fg))]">{form.travel_radius_miles} mi</span>
              </div>
              <Slider data-testid={T.radiusSlider} min={5} max={300} step={5} value={[form.travel_radius_miles]} onValueChange={(v) => set('travel_radius_miles')(v[0])} className="[&_[role=slider]]:bg-[hsl(var(--lxra-gold))] [&_[role=slider]]:border-[hsl(var(--lxra-gold))] [&_.bg-primary]:bg-[hsl(var(--lxra-gold))]" />
            </div>
          </Card>

          <Card title="BRANDING" hint="Your accent tints buttons and chips on your profile page only. LXRA gold stays on the site chrome.">
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
              <input type="color" value={form.accent_color} onChange={(e) => set('accent_color')(e.target.value)} aria-label="Accent color" data-testid={T.accentColor} className="h-10 w-12 rounded-lg border border-[hsl(var(--lxra-border))] bg-transparent cursor-pointer" />
              <Input value={form.accent_color} onChange={(e) => set('accent_color')(e.target.value)} pattern="^#[0-9a-fA-F]{6}$" data-testid={T.accentHex} className={`${fieldClass} lxra-mono uppercase`} />
              <div data-testid={T.accentPreview} style={{ '--studio-accent': accentHsl }} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--lxra-border))] bg-[#0A0A0A] px-3 h-10">
                <span className="inline-flex h-6 px-2 items-center rounded-md text-[10px] uppercase tracking-[0.1em] font-semibold text-[#0A0A0A]" style={{ backgroundColor: 'hsl(var(--studio-accent))' }}>Book</span>
                <span className="text-[10px] uppercase tracking-[0.1em]" style={{ color: 'hsl(var(--studio-accent))' }}>Chip</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[['logo', logoRef, 'h-24 w-24 rounded-2xl'], ['cover', coverRef, 'h-24 w-full rounded-xl']].map(([kind, ref, shape]) => (
                <div key={kind} className="space-y-2">
                  <Label className={labelClass}>{kind === 'logo' ? 'Logo' : 'Cover image'}</Label>
                  <button type="button" onClick={() => ref.current?.click()} data-testid={kind === 'logo' ? T.logoPreview : T.coverPreview} className={`${shape} relative overflow-hidden border border-dashed border-[hsl(var(--lxra-border))] bg-[#141416] hover:border-[hsl(var(--lxra-gold))]/60 transition-colors flex items-center justify-center`}>
                    {profile[`${kind}_url`] ? <img src={profile[`${kind}_url`]} alt="" className="h-full w-full object-cover" /> : (kind === 'logo' ? <Camera className="h-6 w-6 text-[hsl(var(--lxra-muted))]" /> : <ImagePlus className="h-6 w-6 text-[hsl(var(--lxra-muted))]" />)}
                    {uploading === kind && <span className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--lxra-gold))]" /></span>}
                  </button>
                  <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={kind === 'logo' ? T.logoInput : T.coverInput} onChange={(e) => handleUpload(kind, e.target.files?.[0])} />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className={labelClass}>Instagram</Label>
                <Input value={form.instagram} onChange={(e) => set('instagram')(e.target.value)} placeholder="handle (no @)" data-testid={T.instagramInput} className={fieldClass} />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Website</Label>
                <Input value={form.website} onChange={(e) => set('website')(e.target.value)} placeholder="yourstudio.com" data-testid={T.websiteInput} className={fieldClass} />
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Card title="PORTFOLIO" hint={`Feature up to ${MAX_PORTFOLIO} photos from your galleries. Upload photos in Galleries first.`}>
        <p data-testid={T.portfolioCount} className="text-xs text-[hsl(var(--lxra-muted))]">{form.portfolio.length} / {MAX_PORTFOLIO} selected</p>
        {candidates.every((c) => c.images.length === 0) ? (
          <p className="text-sm text-[hsl(var(--lxra-muted))]">No gallery photos yet. Add a gallery and upload photos, then come back to pick your favourites.</p>
        ) : (
          <div data-testid={T.portfolioPicker} className="space-y-5">
            {candidates.filter((c) => c.images.length > 0).map((c) => (
              <div key={c.slug}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))] mb-2">{c.name}{c.is_private ? ' · private' : ''}</p>
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                  {c.images.map((img) => {
                    const on = portfolioIds.has(img.id);
                    return (
                      <button key={img.id} type="button" onClick={() => togglePortfolio(img)} aria-pressed={on} data-testid={T.portfolioCandidate(img.id)} className={`relative aspect-square overflow-hidden rounded-lg border transition-colors ${on ? 'border-[hsl(var(--lxra-gold))]' : 'border-[hsl(var(--lxra-border))] hover:border-[hsl(var(--lxra-gold))]/50'}`}>
                        <img src={img.variants?.social || img.image_url} alt="" className="h-full w-full object-cover" />
                        {on && <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] flex items-center justify-center"><Check className="h-3 w-3" /></span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <AdminAvailabilityCard />
      <AdminReviewsCard />
    </form>
  );
};
